import React, { memo } from 'react';
import {
  ActionButton,
  Text,
  Tooltip,
  TooltipTrigger
} from '@adobe/react-spectrum';
import classNames from 'classnames';
import SavedViewShareLinkIcon from 'app/assets/svgIcons/SavedViewShareLinkIcon';
import type { SavedView } from 'app/services/savedViewsApi';
import sidebarStyles from 'app/components/RoadmapCard/leftSidebar.module.scss';
import { useDelayedTooltip } from 'app/components/MonthlyTimeline/hooks/useDelayedTooltip';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';
import { SavedViewContextMenu } from './SavedViewContextMenu';
import styles from './SavedViewsSection.module.scss';

interface SavedViewListItemProps {
  savedView: SavedView;
  isMenuOpen: boolean;
  isActive: boolean;
  /** Built-in views (e.g. My Stuff) show no actions — no copy-link, no edit/delete menu. */
  isBuiltIn?: boolean;
  onMenuOpenChange: (isOpen: boolean) => void;
  onOpen: (savedView: SavedView) => void;
  onCopyLink: (savedView: SavedView) => void;
  onEdit: (savedView: SavedView) => void;
  onDelete: (savedView: SavedView) => void;
}

export const SavedViewListItem = memo(
  ({
    savedView,
    isMenuOpen,
    isActive,
    isBuiltIn = false,
    onMenuOpenChange,
    onOpen,
    onCopyLink,
    onEdit,
    onDelete
  }: SavedViewListItemProps) => {
    const {
      openTooltip,
      setOpenTooltip,
      handleTooltipMouseEnter,
      handleTooltipMouseLeave
    } = useDelayedTooltip();

    return (
      <div
        className={classNames(
          sidebarStyles['must-wins-audience-item'],
          styles.savedViewItem,
          isMenuOpen && styles.savedViewItemMenuOpen,
          isActive && sidebarStyles['must-wins-audience-item-selected']
        )}
        data-testid={`saved-view-item-${savedView.id}`}
        {...(isMenuOpen ? { 'data-menu-open': true } : {})}
      >
        <div
          className={sidebarStyles['must-wins-item-content']}
          data-testid={`saved-view-name-${savedView.id}`}
        >
          <div className={styles.savedViewNameWrapper}>
            <ToolTipWrapper
              message={savedView.name}
              constrainWidth
              cursor="pointer"
              ariaLabel={savedView.name}
              onPress={() => onOpen(savedView)}
              isOpen={openTooltip === savedView.id}
              onOpenChange={isOpen => {
                if (!isOpen) {
                  setOpenTooltip(null);
                }
              }}
              onMouseEnter={() => handleTooltipMouseEnter(savedView.id)}
              onMouseLeave={handleTooltipMouseLeave}
            >
              <div className={styles.savedViewNameText}>
                <span>{savedView.name}</span>
              </div>
            </ToolTipWrapper>
          </div>
        </div>

        {!isBuiltIn && (
          <div
            className={classNames(
              sidebarStyles['must-wins-link-wrapper'],
              styles.savedViewActions,
              isMenuOpen && styles.savedViewActionsVisible
            )}
          >
            <TooltipTrigger placement="top">
              <ActionButton
                isQuiet
                UNSAFE_className={classNames(
                  sidebarStyles['must-wins-copy-link-button'],
                  styles.savedViewActionButton
                )}
                onPress={() => onCopyLink(savedView)}
                aria-label={`Copy link for ${savedView.name}`}
                data-testid={`saved-view-copy-link-${savedView.id}`}
              >
                <SavedViewShareLinkIcon width={16} height={16} />
              </ActionButton>
              <Tooltip>Copy shareable link</Tooltip>
            </TooltipTrigger>

            <SavedViewContextMenu
              savedViewId={savedView.id}
              savedViewName={savedView.name}
              isOpen={isMenuOpen}
              onOpenChange={onMenuOpenChange}
              onEdit={() => onEdit(savedView)}
              onDelete={() => onDelete(savedView)}
            />
          </div>
        )}
      </div>
    );
  }
);

SavedViewListItem.displayName = 'SavedViewListItem';
